import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import StatCard from '../components/ui/StatCard'
import Button from '../components/ui/Button'
import Card from '../components/ui/Card'
import Icon from '../components/ui/Icon'
import Toggle from '../components/ui/Toggle'
import Calendar from '../components/Calendar'
import AuthGuard from '../components/AuthGuard'
import ProfileCompletionGuard from '../components/ProfileCompletionGuard'
import FajrReminderModal from '../components/FajrReminderModal'
import VerifiedBadge from '../components/VerifiedBadge'
import AddressModal from '../components/AddressModal'
import { useIcon } from '../hooks/useIcons.jsx'
import { APP_CONFIG } from '../data/constants.jsx'
import apiService from '../services/api'

const Profile = () => {
  const { t } = useTranslation()

  return (
    <AuthGuard
      requireAuth={true}
      title={t('profile.loginRequired')}
      description={t('profile.loginMessage')}
    >
      {({ user, logout, openAuth, refreshUser }) => <ProfileContent user={user} logout={logout} openAuth={openAuth} refreshUser={refreshUser} />}
    </AuthGuard>
  )
}

const ProfileContent = ({ user, logout, openAuth, refreshUser }) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [userStats, setUserStats] = useState(null)
  const [userRank, setUserRank] = useState(null)
  const [recentActivity, setRecentActivity] = useState([])
  const [availability, setAvailability] = useState(null)
  const [calendarData, setCalendarData] = useState(null)
  const [detailedStats, setDetailedStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [wokeUpToday, setWokeUpToday] = useState(false)
  const [helperInfo, setHelperInfo] = useState(null)
  const [wakeUpMethod, setWakeUpMethod] = useState(null)
  const [isAvailable, setIsAvailable] = useState(true)
  const [availabilityLoading, setAvailabilityLoading] = useState(false)
  const [wakeupLoading, setWakeupLoading] = useState(false)
  const [wakeUpTimeSettings, setWakeUpTimeSettings] = useState(null)
  const [isWithinWakeUpTime, setIsWithinWakeUpTime] = useState(false)
  const [availableCount, setAvailableCount] = useState(null)
  const [callTimePreferences, setCallTimePreferences] = useState(null)
  const [callTimeLoading, setCallTimeLoading] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showDeleteOwnershipError, setShowDeleteOwnershipError] = useState(false)
  const [showFajrReminder, setShowFajrReminder] = useState(false)
  const [editingAddress, setEditingAddress] = useState(false)
  
  // Map user data with proper field names and defaults
  const mappedUserData = user ? {
    name: user.name || '',
    profilePicture: user.profile_picture || '',
    phone: user.phone_number || '',
    email: user.email || '',
    location: user.country || '',
    joinDate: user.joining_date ? new Date(user.joining_date).toLocaleDateString('bn-BD') : '',
    timezone: user.timezone || 'Asia/Dhaka',
    bio: user.bio || 'এখনো কোনো তথ্য যোগ করা হয়নি।'
  } : {}
  
  // Remove form data hook since editing is disabled
  const userData = mappedUserData

  // Re-sync the user from the server on mount — verification flags (e.g.
  // otp_verified set via the phone-call flow) can change outside this session.
  useEffect(() => { refreshUser?.() }, [refreshUser])

  // Saving the address calls refreshUser(), which dispatches a fresh user
  // object; this effect keys off `user`, so it re-runs. Showing the
  // full-page spinner again threw the whole profile away and scrolled to the
  // top — indistinguishable from a page reload, and it made a save that had
  // in fact succeeded look like it had done nothing. The spinner belongs to
  // the first load only; later refreshes swap the numbers in place.
  const loadedOnce = useRef(false)

  // Load profile data
  useEffect(() => {
    const loadProfileData = async () => {
      if (!user) return

      try {
        if (!loadedOnce.current) setLoading(true)
        const [
          statsData,
          rankData,
          availabilityData,
          activityData,
          calendarResponse,
          detailedStatsData,
          availabilityStatus,
          todayStatus,
          timeSettingsData,
          availableCountData,
          callTimeData
        ] = await Promise.all([
          apiService.getMyStats(),
          apiService.getMyRank().catch(() => null),
          apiService.getUserAvailability(),
          apiService.getRecentActivity(100),
          apiService.getCalendarData(),
          apiService.getDetailedStats(),
          apiService.getAvailabilityStatus().catch(() => ({ is_available: true })),
          apiService.getTodayWakeupStatus().catch(() => ({ is_woke_up_today: false, helper_phone: null })),
          apiService.getWakeUpTimeSettings().catch(() => null),
          apiService.getAvailableMembersNotWokenUp().catch(() => null),
          apiService.getCallTimePreferences().catch(() => null)
        ])

        setUserStats(statsData)
        setUserRank(rankData || {})
        setAvailability(availabilityData)
        setRecentActivity(activityData.activities || [])
        setCalendarData(calendarResponse)
        setDetailedStats(detailedStatsData)
        setIsAvailable(availabilityStatus.is_available)
        setWokeUpToday(todayStatus.is_woke_up_today)
        setHelperInfo(todayStatus.helper_name || todayStatus.helper_phone)
        setWakeUpMethod(todayStatus.helper_phone ? 'helped' : (todayStatus.is_woke_up_today ? 'self' : null))
        setWakeUpTimeSettings(timeSettingsData)
        setAvailableCount(availableCountData)

        // Backend returns response with nested call_time_preferences
        const callTimePrefs = callTimeData?.call_time_preferences || callTimeData
        setCallTimePreferences(callTimePrefs)
      } catch (err) {
        console.error('Failed to load profile data:', err)
        // Same reasoning as the spinner: the full-page error screen is only
        // right when there is nothing to show yet. A refresh that fails must
        // leave the profile the member is already looking at on screen.
        if (!loadedOnce.current) setError(err.message)
      } finally {
        loadedOnce.current = true
        setLoading(false)
      }
    }
    
    loadProfileData()
  }, [user])

  // Check if current time is within wake-up time
  useEffect(() => {
    const checkWakeUpTime = () => {
      if (!wakeUpTimeSettings || !wakeUpTimeSettings.is_active || !wakeUpTimeSettings.service_hours) {
        setIsWithinWakeUpTime(false)
        return
      }

      const now = new Date()
      const currentTime = now.getHours() * 60 + now.getMinutes()
      
      // Parse time strings (e.g., "04:30:00" or "18:00:00")
      const parseTimeString = (timeStr) => {
        if (!timeStr || typeof timeStr !== 'string') {
          console.error('Invalid time string:', timeStr)
          return 0
        }
        const [hours, minutes] = timeStr.split(':').map(Number)
        return hours * 60 + minutes
      }

      const startTime = parseTimeString(wakeUpTimeSettings.service_hours.start_time)
      const endTime = parseTimeString(wakeUpTimeSettings.service_hours.end_time)

      // Handle case where end time is next day (crosses midnight)
      if (endTime < startTime) {
        setIsWithinWakeUpTime(currentTime >= startTime || currentTime <= endTime)
      } else {
        setIsWithinWakeUpTime(currentTime >= startTime && currentTime <= endTime)
      }
    }

    checkWakeUpTime()
    // Check every minute
    const interval = setInterval(checkWakeUpTime, 60000)
    return () => clearInterval(interval)
  }, [wakeUpTimeSettings])

  // Handle calendar month change
  const handleMonthChange = async (month, year) => {
    try {
      const newCalendarData = await apiService.getCalendarData(month, year)
      setCalendarData(newCalendarData)
    } catch (error) {
      console.error('Failed to load calendar data for selected month:', error)
    }
  }

  // Remove onSave function since editing is disabled

  const handleWokeUpToday = async () => {
    if (wakeupLoading) return

    try {
      setWakeupLoading(true)
      const result = await apiService.markWokeUpToday(null)
      setWokeUpToday(true)
      setWakeUpMethod('self')
      setHelperInfo(null)
      alert(`${result.message} পয়েন্ট পেয়েছেন: ${result.points_earned}`)
      // Refresh stats
      const updatedStats = await apiService.getDetailedStats()
      setDetailedStats(updatedStats)
    } catch (err) {
      console.error('Failed to mark woke up:', err)
      // Check if already marked
      if (err.response && err.response.data && err.response.data.error) {
        alert(err.response.data.error)
        // If already marked, update UI state
        if (err.response.data.error.includes('already')) {
          setWokeUpToday(true)
          setWakeUpMethod('self')
        }
      } else {
        alert('তথ্য সংরক্ষণে সমস্যা হয়েছে')
      }
    } finally {
      setWakeupLoading(false)
    }
  }

  // Greet the new member once, when onboarding hands over to the profile.
  // Keyed by user id so a shared browser never shows it to the wrong person,
  // and cleared from history so a refresh cannot re-open it.
  const fajrReminderKey = user?.id ? `fajr:reminderSeen:${user.id}` : null

  useEffect(() => {
    if (!location.state?.showFajrReminder || !fajrReminderKey) return
    if (!localStorage.getItem(fajrReminderKey)) setShowFajrReminder(true)
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state, location.pathname, fajrReminderKey, navigate])

  const handleFajrReminderClose = () => {
    if (fajrReminderKey) localStorage.setItem(fajrReminderKey, '1')
    setShowFajrReminder(false)
  }

  const handleAvailabilityToggle = async (newAvailability) => {
    if (availabilityLoading) return

    try {
      setAvailabilityLoading(true)
      const result = await apiService.toggleAvailability(newAvailability)
      setIsAvailable(result.is_available)
      alert(result.is_available ? 'আপনি এখন সেবার জন্য উপলব্ধ' : 'আপনি এখন সেবার জন্য অনুপলব্ধ')
    } catch (err) {
      console.error('Failed to toggle availability:', err)
      alert('সেটিংস আপডেট করতে সমস্যা হয়েছে')
    } finally {
      setAvailabilityLoading(false)
    }
  }

  const handleCallTimePreferencesUpdate = async (preferences) => {
    if (callTimeLoading) return

    try {
      setCallTimeLoading(true)
      const result = await apiService.updateCallTimePreferences(preferences)

      // Backend returns response with nested call_time_preferences
      const updatedPreferences = result.call_time_preferences || result
      setCallTimePreferences(updatedPreferences)

      // Show success message briefly
      setValidationMessage('')
      const successMessage = 'কল টাইম পছন্দ সফলভাবে সংরক্ষিত হয়েছে'
      alert(successMessage)
    } catch (err) {
      console.error('Failed to update call time preferences:', err)
      alert('কল টাইম পছন্দ আপডেট করতে সমস্যা হয়েছে')
    } finally {
      setCallTimeLoading(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (isDeletingAccount) return
    try {
      setIsDeletingAccount(true)
      await apiService.deleteAccount()
      logout()
      navigate('/')
    } catch (err) {
      setShowDeleteConfirm(false)
      if (err.message && err.message.toLowerCase().includes('transfer ownership')) {
        setShowDeleteOwnershipError(true)
      } else {
        alert('অ্যাকাউন্ট মুছতে পারা যায়নি। পরে চেষ্টা করুন।')
      }
    } finally {
      setIsDeletingAccount(false)
    }
  }

  const validateTimeGap = (startTime, endTime) => {
    if (!startTime || !endTime) return true

    // Convert time strings to minutes for comparison
    const timeToMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number)
      return hours * 60 + minutes
    }

    const startMinutes = timeToMinutes(startTime)
    const endMinutes = timeToMinutes(endTime)

    // Handle overnight time ranges (e.g., 23:00 to 06:00)
    let timeDiff
    if (endMinutes < startMinutes) {
      // Overnight range: calculate as (24*60 - start) + end
      timeDiff = (24 * 60 - startMinutes) + endMinutes
    } else {
      // Same day range
      timeDiff = endMinutes - startMinutes
    }

    return timeDiff >= 30 // Minimum 30 minutes gap
  }

  const [tempTimeSettings, setTempTimeSettings] = useState({
    personal_fajr_start_time: '',
    personal_fajr_end_time: ''
  })
  const [validationMessage, setValidationMessage] = useState('')

  const handleSaveTimePreferences = () => {
    const startTime = tempTimeSettings.personal_fajr_start_time || callTimePreferences.personal_fajr_start_time
    const endTime = tempTimeSettings.personal_fajr_end_time || callTimePreferences.personal_fajr_end_time

    // Validate 30-minute minimum gap
    if (startTime && endTime && !validateTimeGap(startTime, endTime)) {
      setValidationMessage('শুরু এবং শেষ সময়ের মধ্যে কমপক্ষে ৩০ মিনিট ব্যবধান থাকতে হবে')
      return
    }

    // Clear validation message and save
    setValidationMessage('')
    handleCallTimePreferencesUpdate({
      use_personal_call_times: callTimePreferences.use_personal_call_times,
      personal_fajr_start_time: startTime,
      personal_fajr_end_time: endTime
    })

    // Reset temp settings after successful save
    setTempTimeSettings({
      personal_fajr_start_time: '',
      personal_fajr_end_time: ''
    })
  }

  // Check if there are unsaved changes
  const hasUnsavedTimeChanges = () => {
    return tempTimeSettings.personal_fajr_start_time !== '' ||
           tempTimeSettings.personal_fajr_end_time !== ''
  }

  // Update validation message when times change
  const updateValidationMessage = (startTime, endTime) => {
    if (startTime && endTime) {
      if (!validateTimeGap(startTime, endTime)) {
        setValidationMessage('শুরু এবং শেষ সময়ের মধ্যে কমপক্ষে ৩০ মিনিট ব্যবধান থাকতে হবে')
      } else {
        setValidationMessage('')
      }
    } else {
      setValidationMessage('')
    }
  }

  // Real calendar data from API
  const processedCalendarData = calendarData ? {
    currentMonth: calendarData.month - 1, // JS months are 0-indexed
    currentYear: calendarData.year,
    wakeUpDays: Object.keys(calendarData.wake_up_days).filter(day => calendarData.wake_up_days[day]).map(Number),
    streakCount: calendarData.stats?.current_streak || 0,
    bestStreak: calendarData.stats?.best_streak || 0,
    thisMonthTotal: calendarData.stats?.total_days_woke || 0,
    monthlyGoal: calendarData.stats?.monthly_goal || 25
  } : null

  const achievements = detailedStats ? [
    {
      icon: 'Award',
      title: t('profile.fajrChampion'),
      description: `${detailedStats.basic_stats?.total_days_wokeup || 0} ${t('profile.daysFajr')}`,
      gradient: 'bg-gradient-primary'
    },
    {
      icon: 'Users',
      title: t('profile.helper'),
      description: `${detailedStats.basic_stats?.total_days_helped || 0}${t('profile.peopleWoken')}`,
      gradient: 'bg-gradient-golden'
    },
    {
      icon: 'TrendingUp',
      title: t('profile.streak'),
      description: t('profile.maxStreak', { days: detailedStats.streaks?.best_streak || 0 }),
      gradient: 'bg-gradient-primary'
    }
  ] : []

  const profileStats = detailedStats ? [
    {
      icon: 'Award',
      value: detailedStats.basic_stats?.total_points || 0,
      label: t('profile.totalPoints'),
      color: 'text-blue-600'
    },
    {
      icon: 'Calendar',
      value: detailedStats.basic_stats?.total_days_wokeup || 0,
      label: t('profile.totalWakeups'),
      color: 'text-green-600'
    },
    {
      icon: 'Users',
      value: detailedStats.basic_stats?.total_days_helped || 0,
      label: t('profile.helpedOthers'),
      color: 'text-purple-600'
    },
    {
      icon: 'TrendingUp',
      value: userRank?.ranks?.all_time || 'N/A',
      label: t('profile.overallRank'),
      color: 'text-orange-600'
    }
  ] : []


  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-muted-foreground">{t('profile.loadingProfile')}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <strong>{t('profile.errorPrefix')}</strong> {error}
            </div>
            <Button onClick={() => window.location.reload()}>
              {t('common.reload')}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ProfileCompletionGuard>
      <div className="min-h-screen bg-background">
        <Navbar user={user} onLogout={logout} onAuth={openAuth} />

            {/* OTP Verification Warning */}
            {user && !user.otp_verified && (
              <div className="container mx-auto px-4 mt-4">
                <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex items-start sm:items-center">
                      <Icon name="AlertCircle" className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-1 sm:mt-0" />
                      <div>
                        <h3 className="font-semibold text-gray-900 text-sm sm:text-base">{t('profile.verificationWarning')}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">{t('profile.verificationMessage')}</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => navigate('/verify-otp')}
                      className="bg-yellow-600 hover:bg-yellow-700 text-white w-full sm:w-auto text-sm sm:text-base"
                    >
                      <Icon name="Phone" className="w-4 h-4 mr-2" />
                      {t('profile.verifyButton')}
                    </Button>
                  </div>
                </div>
              </div>
            )}

      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">{t('profile.myProfile')}</h1>
          <p className="text-muted-foreground">{t('profile.subtitle')}</p>
        </div>

        <div className="max-w-4xl mx-auto mb-8">
          <Card>
            <Card.Header className="bg-gradient-primary text-white rounded-t-lg">
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center overflow-hidden">
                  {userData.profilePicture ? (
                    <img src={userData.profilePicture} alt={userData.name} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="User" className="w-12 h-12" />
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <h2 className="text-2xl font-bold break-words">{userData.name}</h2>
                    {user?.otp_verified && <VerifiedBadge onDark />}
                  </div>
                  <p className="opacity-90">{t('profile.regularMember')}</p>
                </div>
              </div>
            </Card.Header>

            <Card.Content>
              <div className="space-y-6">
                <h3 className="text-lg font-semibold mb-4">{t('profile.personalInfo')}</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3">
                    <Icon name="Phone" className="w-5 h-5 text-muted-foreground" />
                    <span className="font-medium">{userData.phone}</span>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Icon name="MapPin" className="w-5 h-5 text-muted-foreground shrink-0" />
                    <span className="min-w-0 truncate">
                      {(() => {
                        // thana_name / district_name / division_name arrive as
                        // {en, bn}; show whichever language the member is
                        // reading. Members outside Bangladesh have no ids, only
                        // a free-text city.
                        //
                        // The division is listed too: a member is allowed to
                        // stop after picking one, and without it their save
                        // changed nothing on screen and looked broken.
                        //
                        // Duplicates are dropped because the hierarchy repeats
                        // itself — Savar's district and division are both
                        // "Dhaka", and "সাভার · ঢাকা · ঢাকা" reads as an error.
                        //
                        // Most specific first: the free-text এলাকা (or the city
                        // abroad), then thana, district, division, country.
                        const lang = i18n.language === 'bn' ? 'bn' : 'en'
                        const name = (v) => (v ? v[lang] : null)
                        const parts = [...new Set([
                          user?.short_address,
                          name(user?.thana_name),
                          name(user?.district_name),
                          name(user?.division_name),
                          userData.location,
                        ].filter(Boolean))]
                        return parts.length ? parts.join(' · ') : t('address.notSet')
                      })()}
                    </span>
                    <button
                      onClick={() => setEditingAddress(true)}
                      title={t('address.edit')}
                      aria-label={t('address.edit')}
                      className="shrink-0 text-muted-foreground hover:text-primary transition-colors
                                 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                    >
                      <Icon name="Edit3" className="w-4 h-4" />
                    </button>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Icon name="Calendar" className="w-5 h-5 text-muted-foreground" />
                    <span>{t('profile.memberSince')} {userData.joinDate}</span>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Icon name="Clock" className="w-5 h-5 text-muted-foreground" />
                    <span>{t('profile.timezone')} {userData.timezone}</span>
                  </div>
                </div>
              </div>
            </Card.Content>
          </Card>
        </div>

        {/* Settings Section */}
        <div className="max-w-4xl mx-auto mb-8">
          <h3 className="text-xl font-bold mb-6">{t('profile.settings')}</h3>

          <div className="space-y-4">
            {/* Availability Card */}
            <Card className="p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="Bell" className="w-5 h-5 text-indigo-600" />
                    <h4 className="text-lg font-semibold text-gray-900">{t('profile.availabilityTitle')}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{t('profile.availabilityDesc')}</p>
                </div>
                <Toggle
                  checked={isAvailable}
                  onChange={handleAvailabilityToggle}
                  disabled={availabilityLoading}
                  variant="success"
                  size="default"
                />
              </div>
            </Card>

            {/* Wake Up Status Card */}
            <Card className="p-6 shadow-sm hover:shadow-md transition-shadow">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="Sun" className="w-5 h-5 text-orange-600" />
                  <h4 className="text-lg font-semibold text-gray-900">{t('profile.todayFajr')}</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">{t('profile.howWokeUp')}</p>

                {/* Wake-up Method Toggles */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200 hover:border-green-300 transition-colors bg-white">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="User" className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900 block">{t('profile.wokeUpMyself')}</span>
                        <p className="text-xs text-gray-500">{t('profile.wokeUpMyselfDesc')}</p>
                      </div>
                    </div>
                    <Toggle
                      checked={wakeUpMethod === 'self'}
                      onChange={(checked) => checked && handleWokeUpToday()}
                      disabled={wokeUpToday || wakeupLoading}
                      variant="success"
                      size="default"
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg border-2 border-gray-200 bg-white">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Icon name="Users" className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <span className="font-medium text-gray-900 block">{t('profile.othersWokeMe')}</span>
                        <p className="text-xs text-gray-500">{t('profile.othersWokeMeDesc')}</p>
                      </div>
                    </div>
                    <Toggle
                      checked={wakeUpMethod === 'helped'}
                      onChange={() => {}}
                      disabled={true}
                      variant="success"
                      size="default"
                    />
                  </div>
                </div>

                {/* Helper Information */}
                {wakeUpMethod === 'helped' && helperInfo && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-blue-700">
                      <Icon name="Users" className="w-4 h-4" />
                      <span className="text-sm font-medium">{t('profile.wokeUpBy')} {helperInfo}</span>
                    </div>
                  </div>
                )}

                {/* Success Message */}
                {wokeUpToday && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-green-700">
                      <Icon name="CheckCircle" className="w-5 h-5" />
                      <span className="text-sm font-medium">
                        {wakeUpMethod === 'self' ? t('profile.wokeUpSelfMsg') : t('profile.wokeUpTodayMsg')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
                
              {/* Community Stats */}
              {availableCount && (
                <div className="mt-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-100">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-center">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center space-x-2 text-blue-600 mb-1">
                        <Icon name="Users" className="w-5 h-5" />
                        <span className="font-semibold text-lg">{availableCount.available_not_woken_up || 0}</span>
                      </div>
                      <span className="text-sm text-gray-600">{t('profile.notWokenYet', { count: availableCount.available_not_woken_up || 0 })}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <div className="flex items-center space-x-2 text-purple-600 mb-1">
                        <Icon name="PhoneOff" className="w-5 h-5" />
                        <span className="font-semibold text-lg">{availableCount.available_not_called || 0}</span>
                      </div>
                      <span className="text-sm text-gray-600">{t('profile.notCalledYet', { count: availableCount.available_not_called || 0 })}</span>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Action Buttons Card */}
            <Card className="p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center space-x-2 mb-4">
                <Icon name="Phone" className="w-5 h-5 text-purple-600" />
                <h4 className="text-lg font-semibold text-gray-900">{t('nav.wakeUp')}</h4>
              </div>
              <p className="text-sm text-gray-600 mb-4">আপনার কমিউনিটি বা সবাইকে ফজরের জন্য কল করুন</p>

              <div className="space-y-3">
                <Button
                  onClick={() => window.location.href = '/wake-up'}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 w-full"
                >
                  <Icon name="Users" className="w-5 h-5 mr-2" />
                  {t('profile.wakeEveryone')}
                </Button>

                <Button
                  onClick={() => window.location.href = '/community-wake-up'}
                  className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white px-6 py-3 rounded-lg font-medium shadow-md hover:shadow-lg transition-all duration-200 w-full"
                >
                  <Icon name="Heart" className="w-5 h-5 mr-2" />
                  {t('profile.wakeCommunity')}
                </Button>
              </div>
            </Card>

            {/* Call Time Preferences Card */}
            {callTimePreferences && (
              <Card className="p-6 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-2 mb-4">
                  <Icon name="Clock" className="w-5 h-5 text-indigo-600" />
                  <h4 className="text-lg font-semibold text-gray-900">{t('profile.callTimePreferences')}</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">{t('profile.whenToReceiveCall')}</p>

                {/* Use Personal Call Times Toggle */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Icon name="Settings" className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <span className="font-medium text-gray-900 block">{t('profile.usePersonalTime')}</span>
                      <p className="text-xs text-gray-500">{t('profile.defaultTimeNote')}</p>
                    </div>
                  </div>
                  <Toggle
                    checked={callTimePreferences.use_personal_call_times}
                    onChange={(checked) => {
                      // Backend will handle default values from SystemSettings when enabling personal call times
                      handleCallTimePreferencesUpdate({
                        use_personal_call_times: checked,
                        personal_fajr_start_time: callTimePreferences.personal_fajr_start_time,
                        personal_fajr_end_time: callTimePreferences.personal_fajr_end_time
                      })
                    }}
                    disabled={callTimeLoading}
                    variant="success"
                    size="default"
                  />
                </div>

                {/* Time Settings Display */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-blue-700 mb-2">
                      <Icon name="Sunrise" className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.effectiveStartTime')}</span>
                    </div>
                    <span className="text-xl font-semibold text-blue-800 block">
                      {callTimePreferences.effective_start_time}
                    </span>
                  </div>
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-green-700 mb-2">
                      <Icon name="Sunset" className="w-5 h-5" />
                      <span className="text-sm font-medium">{t('profile.effectiveEndTime')}</span>
                    </div>
                    <span className="text-xl font-semibold text-green-800 block">
                      {callTimePreferences.effective_end_time}
                    </span>
                  </div>
                </div>

                {/* Personal Time Inputs (only visible when use_personal_call_times is true) */}
                {callTimePreferences.use_personal_call_times && (
                  <div className="bg-white border border-gray-200 p-5 rounded-lg space-y-4">
                    <h5 className="font-medium text-gray-900 mb-3">{t('profile.usePersonalTime')}</h5>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                          <Icon name="Sunrise" className="w-4 h-4 text-blue-600" />
                          <span>{t('profile.personalStartTime')}</span>
                        </label>
                        <input
                          type="time"
                          value={tempTimeSettings.personal_fajr_start_time || callTimePreferences.personal_fajr_start_time || ''}
                          onChange={(e) => {
                            const newStartTime = e.target.value
                            setTempTimeSettings({
                              ...tempTimeSettings,
                              personal_fajr_start_time: newStartTime
                            })
                            const endTime = tempTimeSettings.personal_fajr_end_time || callTimePreferences.personal_fajr_end_time
                            updateValidationMessage(newStartTime, endTime)
                          }}
                          disabled={callTimeLoading}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                          <Icon name="Sunset" className="w-4 h-4 text-green-600" />
                          <span>{t('profile.personalEndTime')}</span>
                        </label>
                        <input
                          type="time"
                          value={tempTimeSettings.personal_fajr_end_time || callTimePreferences.personal_fajr_end_time || ''}
                          onChange={(e) => {
                            const newEndTime = e.target.value
                            setTempTimeSettings({
                              ...tempTimeSettings,
                              personal_fajr_end_time: newEndTime
                            })
                            const startTime = tempTimeSettings.personal_fajr_start_time || callTimePreferences.personal_fajr_start_time
                            updateValidationMessage(startTime, newEndTime)
                          }}
                          disabled={callTimeLoading}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                        />
                      </div>
                    </div>

                    {/* Validation Message */}
                    {validationMessage && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-center space-x-2 text-red-700">
                          <Icon name="AlertCircle" className="w-5 h-5" />
                          <span className="text-sm font-medium">{validationMessage}</span>
                        </div>
                      </div>
                    )}

                    {/* System Default Reference */}
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-blue-800 text-sm">
                        <Icon name="Info" className="w-4 h-4" />
                        <span><strong>{t('profile.systemDefault')}</strong> {callTimePreferences.system_fajr_start_time} - {callTimePreferences.system_fajr_end_time}</span>
                      </div>
                    </div>

                    {/* Info Note */}
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center space-x-2 text-amber-800 text-sm">
                        <Icon name="AlertTriangle" className="w-4 h-4" />
                        <span>{t('profile.noteTimeGap')}</span>
                      </div>
                    </div>

                    {/* Save Button */}
                    {hasUnsavedTimeChanges() && (
                      <div className="pt-2">
                        <Button
                          onClick={handleSaveTimePreferences}
                          disabled={callTimeLoading}
                          className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 w-full shadow-sm hover:shadow-md"
                        >
                          <Icon name="Save" className="w-5 h-5 mr-2 inline" />
                          {callTimeLoading ? t('profile.saving') : t('profile.saveTime')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}
            {/* Delete Account Card */}
            <Card className="p-6 shadow-sm border border-red-100">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Trash2" className="w-5 h-5 text-red-600" />
                <h4 className="text-lg font-semibold text-gray-900">অ্যাকাউন্ট</h4>
              </div>
              <p className="text-sm text-gray-500 mb-4">লগ আউট করুন বা অ্যাকাউন্ট স্থায়ীভাবে মুছে ফেলুন।</p>
              <div className="space-y-3">
                <button
                  onClick={logout}
                  className="w-full border-2 border-red-400 text-red-600 hover:bg-red-50 px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center bg-transparent"
                >
                  <Icon name="LogOut" className="w-5 h-5 mr-2" />
                  লগ আউট
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="w-full bg-red-50 hover:bg-red-100 text-red-600 px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
                >
                  <Icon name="Trash2" className="w-5 h-5 mr-2" />
                  অ্যাকাউন্ট মুছে ফেলুন
                </button>
              </div>
            </Card>
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold">{t('profile.statistics')}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {profileStats.map((stat, index) => (
              <StatCard
                key={index}
                icon={useIcon(stat.icon)}
                value={stat.value}
                label={stat.label}
                color={stat.color}
                className="hover:shadow-strong transition-shadow"
              />
            ))}
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-8">
          <h3 className="text-xl font-bold mb-6">{t('profile.calendar')}</h3>
          {processedCalendarData ? (
            <Calendar
              calendarData={processedCalendarData}
              onMonthChange={handleMonthChange}
            />
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {t('profile.loadingCalendar')}
            </div>
          )}
        </div>

        <div className="max-w-4xl mx-auto mb-8">
          <h3 className="text-xl font-bold mb-6">{t('profile.achievements')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {achievements.map((achievement, index) => (
              <Card key={index} className="p-6 text-center">
                <div className={`w-16 h-16 ${achievement.gradient} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Icon name={achievement.icon} className="w-8 h-8 text-white" />
                </div>
                <h4 className="font-bold mb-2">{achievement.title}</h4>
                <p className="text-sm text-muted-foreground">{achievement.description}</p>
              </Card>
            ))}
          </div>
        </div>

        {/* Recent Activities - Moved to bottom */}
        <div className="max-w-4xl mx-auto">
          <h3 className="text-xl font-bold mb-6">{t('profile.recentActivity')}</h3>
          <Card>
            <Card.Content>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {recentActivity.length > 0 ? (
                  recentActivity.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-4 rounded-lg bg-accent/20 hover:bg-accent/30 transition-colors"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-3 h-3 rounded-full ${
                          activity.type === 'self_wakeup' ? 'bg-green-600' :
                          activity.type === 'helped_wakeup' ? 'bg-blue-600' :
                          activity.type === 'wakeup_success' ? 'bg-green-500' :
                          activity.type === 'helped_others' ? 'bg-blue-500' :
                          activity.type === 'call_made' ? 'bg-orange-500' :
                          activity.type === 'call_received' ? 'bg-purple-500' : 'bg-gray-500'
                        }`}></div>
                        <div className="flex-1">
                          <div className="font-medium">{activity.action}</div>
                          <div className="text-sm text-muted-foreground">
                            {activity.date} • {activity.time}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Activity" className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{t('profile.noActivity')}</p>
                  </div>
                )}
              </div>
            </Card.Content>
          </Card>
        </div>
      </div>

        <Footer />
      </div>

      {/* Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-3">অ্যাকাউন্ট মুছে ফেলবেন?</h3>
            <p className="text-sm text-gray-600 mb-6">
              আপনার সকল ডেটা, স্ট্রিক ও কার্যকলাপ স্থায়ীভাবে মুছে যাবে। এটি আর ফেরানো যাবে না।
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeletingAccount}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
              >
                বাতিল
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors disabled:opacity-70 flex items-center"
              >
                {isDeletingAccount ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    মুছে ফেলা হচ্ছে...
                  </>
                ) : 'মুছে ফেলুন'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Community Ownership Error Dialog */}
      {showDeleteOwnershipError && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-start space-x-3 mb-4">
              <Icon name="AlertTriangle" className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <h3 className="text-lg font-bold text-gray-900">কমিউনিটি মালিকানা হস্তান্তর করুন</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              আপনি একটি কমিউনিটির পরিচালক। আগে মালিকানা অন্যকে হস্তান্তর করুন, তারপর অ্যাকাউন্ট মুছুন।
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowDeleteOwnershipError(false)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-medium transition-colors"
              >
                ঠিক আছে
              </button>
            </div>
          </div>
        </div>
      )}
      <FajrReminderModal isOpen={showFajrReminder} onClose={handleFajrReminderClose} />
      <AddressModal
        isOpen={editingAddress}
        user={user}
        onClose={() => setEditingAddress(false)}
        onSaved={async () => {
          setEditingAddress(false)
          if (refreshUser) await refreshUser()
        }}
      />
    </ProfileCompletionGuard>
  )
}

export default Profile